/**
 * Settings screen (DNKADM-0012) — the read-only Configuration overview that
 * replaces the 0006 `settings` placeholder. Owner/Admin only (the route is
 * already wrapped in `RequireAdmin` by 0006; this screen does not re-gate).
 *
 * Three read-only cards, NO writes anywhere on this surface:
 *  1. **Your account** — managed centrally by dink ID; email + name from the
 *     signed-in operator (`useCurrentOperator`). Nothing is editable here.
 *  2. **Location** — the operating location: display name + tenant id, both from
 *     the operator's `tenantSlug` (no separate endpoint).
 *  3. **Cached data** (DNKADM-0045) — a "Refresh cached data" button that clears
 *     the current tenant's server-side read-caches (`POST /v2/admin/caches/clear`),
 *     so freshly-changed data shows immediately instead of after the cache TTL.
 *     Non-destructive (a read-cache reset — nothing is deleted), so no
 *     confirmation dialog; the only write surface on this screen.
 *
 * The whole-tenant Square/payments integration chip (DNKADM-0012) was removed in
 * DNKADM-0042: payment connections are now per service location and multi-provider,
 * so a single whole-tenant "Square: connected" status no longer maps to the model.
 * The per-location connection health indicator moves to the app-bar location
 * selector (tracked in backlog 0011 / DNKADM-0043), not here. Credentials are
 * managed in Payments (`/settings/payments`).
 *
 * DNKADM-0302 U4 adds two cards:
 *  4. **Role & access** — the signed-in role(s) and what they can reach,
 *     DERIVED from `navAccessSummary` (the same `gated` flags that render the
 *     nav, themselves drift-guarded against `router.tsx`) — never a
 *     hand-written permissions list, which would drift. Read-only: roles are
 *     assigned by a platform admin, not here.
 *  5. **Appearance** — the light/dark/system choice. The SAME state as the
 *     app-bar theme control (`useThemeMode`, one stored preference under
 *     `dink_admin_theme_mode`) — surfaced here because the app-bar icon is
 *     unlabelled and the target device has no hover for its tooltip.
 */
import { type ReactNode, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { chipSx, Icon } from '@/theme';
import { useCurrentOperator } from '@/auth/authHooks';
import { useThemeMode, type ThemeMode } from '@/context/theme-mode-context';
import { navAccessSummary } from '@/components/navModel';
import { useToast } from '@/components/useToast';
import { clearCaches } from '@/api/caches';
import { ApiError } from '@/api/errors';
// The shared role badge (DNKADM-0304 U9) — the SAME helper the Members roster
// renders, so one fact cannot wear two treatments.
import { roleBadge } from '@/features/members/roleBadge';

export function SettingsScreen() {
  const operator = useCurrentOperator();
  const themeModePalette = useTheme().palette.mode;
  const { themeMode, setThemeMode } = useThemeMode();

  const email = operator?.email ?? '';
  const name = operator?.name?.trim() ? operator.name : '—';
  const tenantSlug = operator?.tenantSlug?.trim() ? operator.tenantSlug : '—';
  const roles = operator?.roles ?? [];
  // Derived from the nav model's own gating flags — see the header note.
  const access = navAccessSummary(operator?.isAdmin ?? false);

  const showToast = useToast();
  const [refreshing, setRefreshing] = useState(false);

  // Clear the current tenant's server-side read-caches. Non-destructive (no
  // confirm); the `refreshing` flag both shows the in-flight state and guards
  // against a double-fire while in flight. ANY 200 is success (a best-effort
  // partial clear still returns 200); only a non-2xx surfaces an error toast.
  async function handleRefresh() {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const { count } = await clearCaches();
      showToast(count ? `Refreshed ${count} caches` : 'Cached data refreshed.', 'success');
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        showToast('You need OWNER/ADMIN to refresh caches', 'error');
      } else {
        showToast('Couldn’t refresh cached data — try again.', 'error');
      }
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <Box>
      <Typography variant="h1">Settings</Typography>
      <Typography variant="body2" sx={{ mt: 0.5, mb: 2.5 }}>
        Your account and the location you’re operating. This overview is read-only — manage payment
        credentials in Payments.
      </Typography>

      <Stack spacing={2.25}>
        {/* ---- 1. Your account (managed centrally by dink ID) ---- */}
        <SettingsCard
          icon="badge"
          title="Your account"
          description="Managed centrally by your dink ID — nothing is editable here."
        >
          <Field label="Email" value={email} mono />
          <Field label="Name" value={name} />
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 1.5 }}>
            To change your name, password or sign-in, use your dink ID. Staff and roles are
            managed outside this console.
          </Typography>
        </SettingsCard>

        {/* ---- Role & access (DNKADM-0302 U4) — read-only, derived ---- */}
        <SettingsCard
          icon="admin_panel_settings"
          title="Role & access"
          description="What your sign-in can reach in this console. Roles are assigned by a platform admin — not here."
        >
          {/* The role renders as the SAME badge the Members roster uses
              (DNKADM-0304 U9, shared `roleBadge`) — one treatment for one
              fact, unrecognised values included. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' },
              alignItems: 'center',
              py: 0.625,
            }}
          >
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Role</Typography>
            {roles.length > 0 ? (
              <Stack direction="row" sx={{ gap: 0.75, flexWrap: 'wrap' }}>
                {roles.map((role) => {
                  const badge = roleBadge(role, themeModePalette);
                  return <Chip key={role} size="small" label={badge.label} sx={chipSx(badge)} />;
                })}
              </Stack>
            ) : (
              <Typography sx={{ fontSize: 14 }}>—</Typography>
            )}
          </Box>
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
              With your role you can open
            </Typography>
            <Typography sx={{ fontSize: 14, mt: 0.25 }}>{access.reachable.join(', ')}</Typography>
          </Box>
          {access.adminOnly.length > 0 ? (
            <Box sx={{ mt: 1.5 }}>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                Owner/Admin only (not available to your role)
              </Typography>
              <Typography sx={{ fontSize: 14, mt: 0.25 }}>
                {access.adminOnly.join(', ')}
              </Typography>
            </Box>
          ) : null}
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 1.5 }}>
            This list is computed from the same rules that build the navigation menu, so it always
            matches what you actually see.
          </Typography>
        </SettingsCard>

        {/* ---- Appearance (DNKADM-0302 U4) — the SAME theme-mode state as the
             app-bar control; no second stored preference. ---- */}
        <SettingsCard
          icon="contrast"
          title="Appearance"
          description="Light or dark for this console on this device — the same setting as the theme control in the top bar."
        >
          <ToggleButtonGroup
            exclusive
            value={themeMode}
            onChange={(_event, value: ThemeMode | null) => {
              if (value !== null) setThemeMode(value);
            }}
            aria-label="Theme"
            size="small"
          >
            <ToggleButton value="light" sx={{ gap: 0.75, px: 1.75 }}>
              <Icon name="light_mode" sx={{ fontSize: 18 }} />
              Light
            </ToggleButton>
            <ToggleButton value="dark" sx={{ gap: 0.75, px: 1.75 }}>
              <Icon name="dark_mode" sx={{ fontSize: 18 }} />
              Dark
            </ToggleButton>
            <ToggleButton value="system" sx={{ gap: 0.75, px: 1.75 }}>
              <Icon name="contrast" sx={{ fontSize: 18 }} />
              System
            </ToggleButton>
          </ToggleButtonGroup>
        </SettingsCard>

        {/* ---- 2. Location (tenant) ---- */}
        <SettingsCard
          icon="storefront"
          title="Location"
          description="The location these settings apply to."
        >
          <Field label="Location" value={tenantSlug} />
          <Field label="Tenant ID" value={tenantSlug} mono />
        </SettingsCard>

        {/* ---- 3. Cached data (DNKADM-0045) — clear the tenant read-caches ---- */}
        <SettingsCard
          icon="cached"
          title="Cached data"
          description="Force the app to reload the latest staff, locations, and connection data — use this if you’re seeing out-of-date information."
        >
          <Button
            variant="outlined"
            onClick={() => void handleRefresh()}
            disabled={refreshing}
            startIcon={
              refreshing ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Icon name="refresh" sx={{ fontSize: 18 }} />
              )
            }
          >
            {refreshing ? 'Refreshing…' : 'Refresh cached data'}
          </Button>
        </SettingsCard>
      </Stack>
    </Box>
  );
}

/** A titled read-only `Paper` card with an icon header. */
function SettingsCard({
  icon,
  title,
  description,
  children,
}: {
  icon: string;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <Paper elevation={1} sx={{ overflow: 'hidden' }}>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          gap: 1.5,
          p: '16px 20px',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
        <Box
          aria-hidden
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: 2,
            bgcolor: 'background.default',
            color: 'primary.main',
            flexShrink: 0,
          }}
        >
          <Icon name={icon} sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 500 }}>{title}</Typography>
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{description}</Typography>
        </Box>
      </Stack>
      <Box sx={{ p: '18px 20px' }}>{children}</Box>
    </Paper>
  );
}

/** A read-only label/value row (never an input — this screen edits nothing). */
function Field({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' },
        alignItems: 'baseline',
        py: 0.625,
      }}
    >
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{label}</Typography>
      <Typography
        sx={{ fontSize: 14, ...(mono ? { fontFamily: 'monospace', fontSize: 12.5 } : {}) }}
      >
        {value}
      </Typography>
    </Box>
  );
}
